import { Component, Input, OnInit } from '@angular/core';
import { ICompanyEngagementHistory } from '../../../message-models/company-engagement-history.interface';
import { IRiskInfo } from '../../../message-models/risk-info.interface';
import { IJobLevelInfo } from '../../../message-models/job-level.interface';
import { IJobTitleInfo } from '../../../message-models/job-title.interface';
import { IDateTimeWithZone } from '../../../common/date-time-with-zone.interface';
import { ElectricalWorkType } from '../../../message-models/electrical-work-type';
import { IEmploymentStatus } from '../../../message-models/employment-status.interface';
import { consts } from '../../../common/modals/consts';
import { CompanyEngagementHistory } from '../../../message-models/company-engagement-history.class';
import { IOperativeTypeInfo } from '../../../message-models/operative-type.interface';
import { IBusinessUnitInfo } from '../../../message-models/business-unit.interface';
import { ICompanyJobTitle } from '../../../message-models/company-job-title.interface';

@Component({
  selector: 'app-engagement-history-editor',
  templateUrl: './engagement-history-editor.component.html',
  styleUrls: ['./engagement-history-editor.component.scss']
})
export class EngagementHistoryEditorComponent implements OnInit {
  @Input() isEditing: boolean = false;
  @Input() engagement: CompanyEngagementHistory;
  @Input() allOperativeTypes: IOperativeTypeInfo[] = [];
  @Input() allBusinessUnits: IBusinessUnitInfo[] = [];
  @Input() allJobTitles: IJobTitleInfo[] = [];
  @Input() allCompanyJobTitles: ICompanyJobTitle[] = [];
  @Input() allJobLevels: IJobLevelInfo[] = [];
  @Input() allRisks: IRiskInfo[] = [];
  @Input() allEmploymentStatuses: IEmploymentStatus[] = [];
  @Input() saveAttempted: boolean = false;

  //Filtered lists based on current state
  filteredJobLevels: IJobLevelInfo[] = [];
  filteredJobTitles: IJobTitleInfo[] = [];
  filteredRisks: IRiskInfo[] = [];

  //Selected items
  selectedBusinessUnitId: string;
  selectedOperativeTypeId: string;
  selectedJobTitleId: string;
  selectedJobLevelId: string;
  selectedRiskId: string;
  defaultJobJevelId: string;

  //Used to hold changes to engagement
  tempEngagement: CompanyEngagementHistory = new CompanyEngagementHistory;
  businessUnitHelpText: string;
  isNonElectricalJobRole: boolean;
  isQsRole: boolean;
  operativeMaxDate: Date;
  operativeMinDate: Date;
  showJobTitleUpdate: boolean;
  showJobLevelUpdate: boolean;
  showRiskUpdate: boolean;


  constructor() {
  }

  ngOnInit(): void {
    if (this.engagement && this.engagement.from) {
      this.disableDatesPriorSelected(this.engagement?.from);
    }
    if (!this.engagement.id || this.engagement.id === consts.emptyGuid) {
      const defaultEmployment = this.allEmploymentStatuses.find(c => c.isDefault === true);
      if (defaultEmployment) {
        this.engagement.employmentStatusId = defaultEmployment.id;
      }
    }

    this.tempEngagement = this.engagement;
    this.selectedBusinessUnitId = this.allBusinessUnits.find(f => f.id === this.engagement?.businessUnitId)?.id;
    this.selectedOperativeTypeId = this.allOperativeTypes.find(f => f.id === this.engagement?.operativeTypeId)?.id;
    this.selectedJobTitleId = this.allJobTitles.find(f => f.id === this.engagement?.jobTitleId)?.id;
    this.selectedJobLevelId = this.allJobLevels.find(f => f.id == this.engagement?.jobLevelId)?.id;
    this.selectedRiskId = this.allRisks.find(f => f.id == this.engagement?.riskId)?.id;

    this.updateJobTitles(this.engagement.operativeTypeId, false);
    this.businessUnitHelpText = this.allBusinessUnits?.length < 1 ? "You have no Business Units defined. This field is optional but if you wish to create some business units then please go to the Business Units tab on the settings page." : "Choose the Business Unit that best suits the Operatives current role.";
  }

  disableDatesPriorSelected(date: IDateTimeWithZone) {
    let selectedDate = new Date(date.dateTime);
    this.operativeMinDate = new Date(selectedDate.setDate(selectedDate.getDate() + 1));
  }

  disableDatesPostSelected(date: IDateTimeWithZone) {
    let selectedDate = new Date(date.dateTime);
    this.operativeMaxDate = new Date(selectedDate.setDate(selectedDate.getDate() - 1));
  }

  updateJobTitles(operativeTypeId: string, showUpdateAlert: boolean) {
    this.tempEngagement.operativeTypeId = operativeTypeId;//ensure correct operative typeid
    if (this.tempEngagement.operativeTypeId == "4eff2451-6793-4590-82c1-fc189dc47859") {
      this.filteredJobTitles = this.allCompanyJobTitles;
      this.selectedJobTitleId = this.allCompanyJobTitles.find(f => f.id == this.tempEngagement.jobTitleId)?.id;
      if (!this.selectedJobTitleId) { //job title not found in list so default to the first in the list
        this.selectedJobTitleId = this.allCompanyJobTitles[0].id;
        this.tempEngagement.jobTitleId = this.selectedJobTitleId;
      }
    }
    else {
      this.filteredJobTitles = this.allJobTitles.filter(f => f.operativeTypeId == this.tempEngagement.operativeTypeId);
      this.selectedJobTitleId = this.filteredJobTitles.find(f => f.id === this.tempEngagement.jobTitleId)?.id;

      if (!this.selectedJobTitleId) { //job title not found in list so default to the first in the list
        this.selectedJobTitleId = this.filteredJobTitles[0].id;
        this.tempEngagement.jobTitleId = this.selectedJobTitleId;
      }
    }
    //Complete other tasks
    this.showJobTitleUpdate = showUpdateAlert;
    this.updateJobLevel(this.tempEngagement.jobTitleId, showUpdateAlert);
  }

  updateJobLevel(jobTitleId: string, showUpdateAlert: boolean) {
    this.showJobLevelUpdate = showUpdateAlert;
    this.tempEngagement.jobTitleId = jobTitleId;
    this.isQsRole = this.filteredJobTitles.find(d => d.id === jobTitleId)?.electricalWorkType == ElectricalWorkType.QualifiedSupervisor;
    this.isNonElectricalJobRole = this.filteredJobTitles.find(d => d.id === jobTitleId)?.electricalWorkType == ElectricalWorkType.NonElectrical;
    this.defaultJobJevelId = this.filteredJobTitles.find(d => d.id === jobTitleId)?.defaultJobLevelId;

    if (this.isNonElectricalJobRole) {
      this.filteredJobLevels = this.allJobLevels.filter(f => f.index === 0);
    }
    else if (this.isQsRole) {
      this.filteredJobLevels = this.allJobLevels.filter(f => f.id == this.defaultJobJevelId);
    }
    else {
      this.filteredJobLevels = this.allJobLevels.filter(f => f.operativeTypeId == this.tempEngagement.operativeTypeId).sort((a, b) => a.index - b.index);
    }
    this.selectedJobLevelId = this.filteredJobLevels.find(x => x.id === this.defaultJobJevelId)?.id;

    //job level not found so set to default value for the selected job title
    if (!this.selectedJobLevelId) {
      this.selectedJobLevelId = this.filteredJobLevels.find(x => x.id === this.defaultJobJevelId)?.id;
    }
    this.tempEngagement.jobLevelId = this.selectedJobLevelId;
    this.updateRisk(this.tempEngagement.jobLevelId, showUpdateAlert);
  }

  updateRisk(jobLevelId: string, showUpdateAlert: boolean) {
    this.showRiskUpdate = showUpdateAlert;
    this.tempEngagement.jobLevelId = jobLevelId;
    if (this.isNonElectricalJobRole) {
      this.filteredRisks = this.allRisks.filter(f => f.index === 0);
    }
    else {
      this.filteredRisks = this.allRisks.filter(f => f.operativeTypeId == this.tempEngagement.operativeTypeId).sort((a, b) => a.index - b.index);
    }
    this.selectedRiskId = this.filteredRisks.find(x => x.id === this.tempEngagement.riskId)?.id;
    //risk not found so set to first value in filtered list
    if (!this.selectedRiskId) {
      this.selectedRiskId = this.filteredRisks[0].id;
    }
    this.tempEngagement.riskId = this.selectedRiskId;
  }  
}
